import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterMember = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [nextMemberId, setNextMemberId] = useState('');
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    initials: '',
    designation: '',
    company_name: '',
    contact_no: '',
    whatsapp_no: '',
    email: '',
    address: '',
    pincode: '',
    state: '',
    district: '',
    expiry_date: '',
    member_type: '',
    customer_type: 'Member',
    plan_type: '',
    database_type: '',
    subscription_duration: '',
    amount_paid: '',
    status: 'ACTIVE',
    notes: '',
    category: '',
    delivery: ''
  });

  // Auto-populate WhatsApp number from contact number
  const handleContactChange = (value) => {
    setFormData(prev => {
      const newData = { ...prev, contact_no: value };
      // Auto-populate WhatsApp number if empty
      if (!prev.whatsapp_no || prev.whatsapp_no === '') {
        const digits = value.replace(/\D/g, '');
        if (digits.length >= 10) {
          newData.whatsapp_no = digits.slice(-10);
        }
      }
      return newData;
    });
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    // Fetch member ID when required fields change
    if (formData.database_type && formData.customer_type) {
      // For Complimentary, we don't need plan_type
      if (formData.customer_type === 'Complimentary') {
        fetchNextMemberId();
      } else if (formData.plan_type) {
        // For Member, we need plan_type
        fetchNextMemberId();
      }
    }
  }, [formData.database_type, formData.customer_type, formData.plan_type, formData.category]);

  // Auto-calculate amount based on plan_type and subscription_duration
  useEffect(() => {
    if (formData.plan_type && formData.subscription_duration) {
      const amount = calculateAmount(formData.plan_type, formData.subscription_duration);
      setFormData(prev => ({ ...prev, amount_paid: amount }));
    }
  }, [formData.plan_type, formData.subscription_duration]);

  const calculateAmount = (planType, duration) => {
    const pricing = {
      'Registered Post': { '1': '2000', '2': '3600', '3': '5000', '15': '15000' },
      'Ordinary Post': { '1': '1500', '2': '3600', '3': '4000', '15': '10000' },
      'E-Magazine': { '1': '1000', '2': '1600', '3': '2000', '15': '8000' },
      'Life': { '15': '10000' }
    };
    
    return pricing[planType]?.[duration] || '';
  };

  const fetchNextMemberId = async () => {
    try {
      const response = await axios.get(`${API}/members/next-id`, {
        params: {
          magazine: formData.database_type,
          customer: formData.customer_type,
          plan_type: formData.plan_type || 'Registered Post',
          category: formData.category || ''
        }
      });
      setNextMemberId(response.data.next_id);
    } catch (error) {
      console.error('Error fetching next member ID:', error);
      toast.error('Failed to fetch next member ID');
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Failed to fetch states');
    }
  };

  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      const response = await axios.post(`${API}/members/unique-districts`, { state: state });
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.state) {
      fetchDistricts(formData.state);
    } else {
      setDistricts([]);
    }
  }, [formData.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic required fields
    if (!formData.name || !formData.email || !formData.database_type || !formData.customer_type) {
      toast.error('Please fill in all required fields (Name, Email, Magazine, Customer Type)');
      return;
    }

    // If Member type, plan_type is required
    if (formData.customer_type === 'Member' && !formData.plan_type) {
      toast.error('Please select a Plan Type for Member');
      return;
    }

    // Convert expiry_date from yyyy-mm-dd to dd/mm/yyyy
    const convertToStorageFormat = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
      return dateStr;
    };

    // Auto-set member_type from plan_type for backend compatibility
    const submissionData = {
      ...formData,
      member_type: formData.plan_type || formData.customer_type || 'Regular',
      expiry_date: convertToStorageFormat(formData.expiry_date)
    };

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/members`, submissionData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(`Member registered successfully with ID: ${nextMemberId}`);
      
      // Reset form
      setFormData({
        name: '',
        initials: '',
        designation: '',
        company_name: '',
        contact_no: '',
        email: '',
        address: '',
        pincode: '',
        state: '',
        district: '',
        expiry_date: '',
        member_type: '',
        plan_type: '',
        database_type: '',
        subscription_duration: '',
        amount_paid: '',
        status: 'ACTIVE',
        notes: '',
        category: '',
        delivery: ''
      });
      setNextMemberId('');
    } catch (error) {
      console.error('Error registering member:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to register member';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="register-title">
                Register Member
              </h1>
            </div>
            {nextMemberId && (
              <div className="text-xl font-semibold" style={{ color: 'var(--primary-blue)' }}>
                Next ID: {nextMemberId}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="form-section">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="database_type">Magazine *</Label>
              <Select value={formData.database_type} onValueChange={(value) => setFormData({ ...formData, database_type: value })}>
                <SelectTrigger data-testid="database-type-select">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_type">Customer Type *</Label>
              <Select value={formData.customer_type} onValueChange={(value) => setFormData({ ...formData, customer_type: value })}>
                <SelectTrigger data-testid="customer-type-select">
                  <SelectValue placeholder="Select customer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Complimentary">Complimentary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.customer_type === 'Member' && (
              <div className="space-y-2">
                <Label htmlFor="plan_type">Plan Type *</Label>
                <Select value={formData.plan_type} onValueChange={(value) => setFormData({ ...formData, plan_type: value })}>
                  <SelectTrigger data-testid="plan-type-select">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.database_type === 'Aabkari Times ENG' ? (
                      <>
                        <SelectItem value="Registered Post">Registered Post</SelectItem>
                        <SelectItem value="E-Magazine">E-Magazine</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="Registered Post">Registered Post</SelectItem>
                        <SelectItem value="E-Magazine">E-Magazine</SelectItem>
                        <SelectItem value="Member-Advt.">Member-Advt.</SelectItem>
                        <SelectItem value="Life">Life</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="initials">Initials</Label>
              <Select value={formData.initials} onValueChange={(value) => setFormData({ ...formData, initials: value })}>
                <SelectTrigger data-testid="initials-select">
                  <SelectValue placeholder="Select initials" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                  <SelectItem value="M/s">M/s</SelectItem>
                  <SelectItem value="M/s.">M/s.</SelectItem>
                  <SelectItem value="Mr">Mr</SelectItem>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Mrs">Mrs</SelectItem>
                  <SelectItem value="Mrs.">Mrs.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Prof.">Prof.</SelectItem>
                  <SelectItem value="Shri">Shri</SelectItem>
                  <SelectItem value="Shri.">Shri.</SelectItem>
                  <SelectItem value="Smt.">Smt.</SelectItem>
                  <SelectItem value="Sri.">Sri.</SelectItem>
                  <SelectItem value="The">The</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="designation">Designation</Label>
              <Input
                id="designation"
                data-testid="designation-input"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="Enter designation"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                data-testid="company-name-input"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Enter company name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_no">Contact Number</Label>
              <Input
                id="contact_no"
                data-testid="contact-input"
                value={formData.contact_no}
                onChange={(e) => handleContactChange(e.target.value)}
                placeholder="Enter contact number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp_no">WhatsApp Number</Label>
              <Input
                id="whatsapp_no"
                data-testid="whatsapp-input"
                value={formData.whatsapp_no}
                onChange={(e) => setFormData({ ...formData, whatsapp_no: e.target.value.replace(/\D/g, '').slice(-10) })}
                placeholder="10-digit WhatsApp number"
                maxLength={10}
              />
              <p className="text-xs text-gray-500">Auto-filled from contact. Country code +91 assumed.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="text"
                data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email (multiple emails separated by comma)"
                required
              />
              <p className="text-xs text-gray-500">You can enter multiple emails separated by comma. Example: email1@example.com, email2@example.com</p>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">Full Address</Label>
              <Input
                id="address"
                data-testid="address-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter full address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="district">District</Label>
              <Input
                id="district"
                data-testid="district-input"
                list="district-options"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                placeholder={formData.state ? "Select or type district" : "Select state first"}
                disabled={!formData.state}
              />
              <datalist id="district-options">
                {districts.map(district => (
                  <option key={district} value={district} />
                ))}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select value={formData.state} onValueChange={(value) => {
                setFormData({ ...formData, state: value, district: '' });
                fetchDistricts(value);
              }}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode (6 digits)</Label>
              <Input
                id="pincode"
                data-testid="pincode-input"
                type="text"
                maxLength={6}
                value={formData.pincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                  setFormData({ ...formData, pincode: value });
                }}
                placeholder="Enter 6 digit pincode"
              />
            </div>

            {formData.customer_type === 'Member' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    data-testid="expiry-date-input"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subscription_duration">Subscription Duration (Years)</Label>
                  <Select value={formData.subscription_duration} onValueChange={(value) => setFormData({ ...formData, subscription_duration: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Year</SelectItem>
                      <SelectItem value="2">2 Years</SelectItem>
                      <SelectItem value="3">3 Years</SelectItem>
                      {formData.plan_type === 'Life' && <SelectItem value="15">15 Years (Life)</SelectItem>}
                      {formData.plan_type !== 'Life' && <SelectItem value="15">15 Years</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount_paid">Amount (₹)</Label>
                  <Input
                    id="amount_paid"
                    type="number"
                    value={formData.amount_paid || ''}
                    onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
                    placeholder="Enter amount or auto-calculated"
                  />
                  <p className="text-xs text-gray-500">Auto-fills based on Plan & Duration. You can override.</p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.customer_type === 'Member' && (
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Life">Life</SelectItem>
                    <SelectItem value="Regular">Regular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="delivery">Delivery</Label>
              <Select value={deliveryLabel(formData.delivery)} onValueChange={(value) => setFormData({ ...formData, delivery: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select delivery method" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Enter any additional notes"
                className="w-full min-h-[100px] p-2 border rounded-md"
                rows="4"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="submit" disabled={loading} className="btn-primary" data-testid="submit-button">
              <Save className="mr-2" size={18} />
              {loading ? 'Registering...' : 'Register Member'}
            </Button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default RegisterMember;